import { MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { RecordLink } from 'components/record-chip/RecordLink';
import React from 'react';
import { ClassifierData } from 'types/classifiers';
import { InvoiceTimecard } from 'types/invoice-details';
import { NamedEntityMinDto } from 'types/other';
import { getClassifierText } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { InvoiceDetailsParameterizedTracker } from '../../InvoiceDetailsChangeTrackers';
import { ItemEditButton } from '../../InvoiceItemRow';
import styles from './EmployeeDisplay.module.css';
import { TimecardIssueIndicator } from './TimecardIssueIndicator';

type Props = {
    hideEditButton: boolean;
    employee: NamedEntityMinDto | null;
    timecardDate: string | null;
    classifiers: Record<string, ClassifierData>;
    invoiceTimecard: InvoiceTimecard;
    timecardActivityTypeCode: string | null;
    isNonBillable: boolean | undefined;
    handleOnClickEditButton: () => void;
    buttonsDisabled: boolean;
};

export const EmployeeDisplay = React.memo(
    ({
        hideEditButton,
        employee,
        timecardDate,
        classifiers,
        invoiceTimecard,
        timecardActivityTypeCode,
        isNonBillable,
        handleOnClickEditButton,
        buttonsDisabled,
    }: Props) => {
        const { timecard } = invoiceTimecard;
        const formatName = useEmployeeNameFormatter();

        return (
            <div
                className={`${styles.container} ${!hideEditButton ? styles.containerClickable : ''} opacity-decreasing-when-disabled`}
            >
                <div>
                    <div className={`${styles.nameRow} opacity-decreasing-when-disabled`}>
                        <TimecardIssueIndicator issues={invoiceTimecard.issues} />
                        {invoiceTimecard.employee && (
                            <RecordLink
                                to={`/employee/${invoiceTimecard.employee.id}`}
                                className={`${styles.employeeLink} text-fg hover:text-link w-fit no-underline hover:underline`}
                                label={formatName(invoiceTimecard.employee)}
                            />
                        )}
                        <InvoiceDetailsParameterizedTracker
                            currentParam={employee?.id}
                            displayValue={timecard?.employee ? formatName(timecard.employee) : undefined}
                            storedParam={timecard?.employee?.id}
                            textWrap="nowrap"
                        />
                    </div>
                    <div className={styles.metaRow}>
                        <span className={styles.date}>
                            <FormattedDate value={timecardDate} fallback="" />
                        </span>
                        {isNonBillable ? (
                            <span className={styles.nonBillableIcon}>
                                <MoneyOffRoundedIcon
                                    fontSize="small"
                                    className={`${styles.nonBillableIconSvg} text-primary`}
                                />
                            </span>
                        ) : null}
                        {(timecardActivityTypeCode || timecard.timecardActivityTypeCode) && (
                            <span className={styles.activityChip}>
                                {getClassifierText(
                                    classifiers['TIMECARD_ACTIVITY_TYPE'],
                                    timecardActivityTypeCode || timecard.timecardActivityTypeCode
                                )}
                            </span>
                        )}
                    </div>
                </div>
                {!hideEditButton ? (
                    <ItemEditButton onClick={handleOnClickEditButton} disabled={buttonsDisabled} />
                ) : null}
            </div>
        );
    }
);
